/**
 * Authentication Context
 * Manages user authentication and role-based access
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';
import employeeService from '../services/employeeService';
import auditService from '../services/auditService';
import { comparePassword } from '../utils/passwordUtils';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('tokenPlanner_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        logger.info('User session restored from storage', { userId: userData.id });
      } catch (error) {
        logger.error('Error parsing stored user data', error);
        localStorage.removeItem('tokenPlanner_user');
      }
    }
    setLoading(false);
  }, []);

  /**
   * Login user
   */
  const login = async (email, password) => {
    try {
      logger.info('Login attempt', { email, operation: 'login' });
      
      // Find employee by email (case-insensitive)
      const employees = await employeeService.getAllEmployees();
      const employee = employees.find(emp => emp.email.toLowerCase() === email.toLowerCase());
      
      if (!employee) {
        logger.warning('Login failed: Employee not found', { email });
        throw new Error('Invalid email or password');
      }
      
      // Validate password if password hash exists
      if (employee.passwordHash) {
        const isPasswordValid = await comparePassword(password, employee.passwordHash);
        if (!isPasswordValid) {
          logger.warning('Login failed: Invalid password', { email });
          throw new Error('Invalid email or password');
        }
      } else {
        // If no password hash exists, allow login (for backward compatibility)
        // But log a warning
        logger.warning('Login without password hash (legacy user)', { email });
      }
      
      // Store user in localStorage
      const userData = {
        id: employee.id,
        email: employee.email,
        name: employee.name,
        role: employee.role,
        managerId: employee.managerId,
        yearlyTokens: employee.yearlyTokens,
        tokensUsed: employee.tokensUsed || 0,
      };
      
      localStorage.setItem('tokenPlanner_user', JSON.stringify(userData));
      setUser(userData);
      
      // Log audit
      await auditService.logAudit({
        userId: employee.id,
        userRole: employee.role,
        action: 'login',
        entityType: 'auth',
        details: { email },
      });
      
      logger.info('Login successful', { userId: employee.id, role: employee.role });
      
      return userData;
    } catch (error) {
      logger.error('Login error', error, { email, operation: 'login' });
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      if (user) {
        logger.info('Logout', { userId: user.id, operation: 'logout' });
        
        // Log audit
        await auditService.logAudit({
          userId: user.id,
          userRole: user.role,
          action: 'logout',
          entityType: 'auth',
        });
      }
      
      localStorage.removeItem('tokenPlanner_user');
      setUser(null);
    } catch (error) {
      logger.error('Logout error', error, { operation: 'logout' });
    }
  };

  /**
   * Update user data
   */
  const updateUser = async (updates) => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }
      
      logger.info('Updating user data', { userId: user.id, updates, operation: 'updateUser' });
      
      const updatedEmployee = await employeeService.updateEmployee(user.id, updates);
      
      const updatedUser = {
        ...user,
        ...updates,
        yearlyTokens: updatedEmployee.yearlyTokens,
        tokensUsed: updatedEmployee.tokensUsed,
      };
      
      localStorage.setItem('tokenPlanner_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      logger.info('User data updated', { userId: user.id, operation: 'updateUser' });
      
      return updatedUser;
    } catch (error) {
      logger.error('Error updating user data', error, { operation: 'updateUser' });
      throw error;
    }
  };

  /**
   * Update user password
   */
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }
      
      logger.info('Updating user password', { userId: user.id, operation: 'updatePassword' });
      
      // Get employee to verify current password
      const employee = await employeeService.getEmployeeById(user.id);
      if (!employee) {
        throw new Error('User not found');
      }
      
      // Verify current password if password hash exists
      if (employee.passwordHash) {
        const isPasswordValid = await comparePassword(currentPassword, employee.passwordHash);
        if (!isPasswordValid) {
          throw new Error('Current password is incorrect');
        }
      }
      
      // Validate new password
      if (!newPassword || newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters long');
      }
      
      // Update password
      await employeeService.updateEmployee(user.id, { password: newPassword });
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'update',
        entityType: 'password',
        details: { changed: true },
      });
      
      logger.info('Password updated successfully', { userId: user.id, operation: 'updatePassword' });
      
      return true;
    } catch (error) {
      logger.error('Error updating password', error, { operation: 'updatePassword' });
      throw error;
    }
  };

  /**
   * Check if user has role
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user is admin
   */
  const isAdmin = () => {
    return hasRole('admin');
  };

  /**
   * Check if user is manager
   */
  const isManager = () => {
    return hasRole('manager') || isAdmin();
  };

  /**
   * Check if user is employee
   */
  const isEmployee = () => {
    return hasRole('employee') || isManager();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    updatePassword,
    hasRole,
    isAdmin,
    isManager,
    isEmployee,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

